import { h } from '@omni-torn/ui';
import { server } from '../session';
import { box, withTableCsv } from '../ui';
import { serverCapacityBox } from './server-capacity';

/**
 * Bot stats — owner only (owner's call 2026-11-02: "an owner only section on the website where I can track how many
 * servers its on how many commands its seen how many giveaways and all other stats for the bot"). From
 * GET /api/owner/bot-stats (the bot's private /sync/stats): numbers only — no server names, no people.
 */

interface Day {
  day: string;
  servers: number | null;
  linked: number | null;
  commands: number;
  buttons: number;
  lookups: { faction: number; lent: number; own: number };
  watch: number;
  alerts: number;
  dms: number;
  refused: number;
  giveawaysStarted: number;
  roundAvgMs: number | null;
  roundMaxMs: number | null;
}
interface Stats {
  at: number;
  servers: { total: number; approved: number; setUp: number; running: number; closed: number; waiting: number; byKind: Record<'faction' | 'factions' | 'community' | 'none', number> };
  linked: number;
  commands: { today: number; days: number; perCommand: Array<{ name: string; n: number }> };
  giveaways: { running: number; finished: number; cancelled: number; entries: number; startedToday: number };
  round: { lastMs: number; factions: number; skipped: number } | null;
  history: Day[];
}

const fmt = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString());
const sec = (ms: number | null | undefined) => (ms === null || ms === undefined ? '—' : `${(ms / 1000).toFixed(1)} s`);

function tiles(items: Array<[string, string, string]>): HTMLElement {
  return h('div', { class: 'bot-health-grid' }, ...items.map(([label, value, field]) => h('div', { 'data-stat': field }, h('strong', {}, value), h('span', { class: 'muted small' }, label))));
}

/** A bar this wide (%), set through the style object (the site's CSP allows no inline style attributes). */
function bar(pct: number): HTMLElement {
  const b = h('span', { class: 'stat-bar' });
  b.style.width = `${pct}%`;
  return b;
}

/** One row per day with a bar (cheap: plain elements, scaled to the busiest day). */
function bars(title: string, days: Day[], value: (d: Day) => number, field: string): HTMLElement {
  const max = Math.max(1, ...days.map(value));
  return h(
    'div',
    { class: 'stat-bars', 'data-chart': field },
    h('h3', {}, title),
    ...days.map((d) =>
      h(
        'div',
        { class: 'stat-bar-row' },
        h('span', { class: 'muted small stat-bar-day' }, d.day.slice(5)),
        h('span', { class: 'stat-bar-track' }, bar(Math.round((value(d) / max) * 100))),
        h('span', { class: 'small stat-bar-n' }, fmt(value(d))),
      ),
    ),
  );
}

export function renderBotStats(view: HTMLElement): void {
  const body = h('div', { 'data-field': 'bot-stats' }, h('p', { class: 'muted' }, 'Loading…'));
  // The machine the bot and the server share (owner's ask 2026-11-02): its disk, memory and CPU on top.
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Bot stats')), serverCapacityBox(), body);
  void server<{ bot: boolean; stats: Stats | null }>('GET', '/api/owner/bot-stats')
    .then((r) => {
      const x = r.stats;
      if (!x) return body.replaceChildren(h('p', { class: 'notice bad', 'data-field': 'stats-down' }, 'The bot isn’t answering.'));
      const today = x.history[x.history.length - 1];
      const days = x.history.slice(-31);
      body.replaceChildren(
        box('Servers', [
          tiles([
            ['servers the bot is in', fmt(x.servers.total), 'servers'],
            ['approved', fmt(x.servers.approved), 'approved'],
            ['set up', fmt(x.servers.setUp), 'set-up'],
            ['parts running', fmt(x.servers.running), 'running'],
            ['waiting for approval', fmt(x.servers.waiting), 'waiting'],
            ['closed (blocked or full)', fmt(x.servers.closed), 'closed'],
            ['faction servers', fmt(x.servers.byKind.faction), 'kind-faction'],
            ['several factions', fmt(x.servers.byKind.factions), 'kind-factions'],
            ['community', fmt(x.servers.byKind.community), 'kind-community'],
            ['kind not picked', fmt(x.servers.byKind.none), 'kind-none'],
            ['members linked', fmt(x.linked), 'linked'],
          ]),
        ], { id: 'stats-servers' }),
        box('Today', [
          tiles([
            ['commands', fmt(x.commands.today), 'commands-today'],
            ['buttons and forms', fmt(today?.buttons), 'buttons-today'],
            ['look-ups — faction keys', fmt(today?.lookups.faction), 'lookups-faction'],
            ['look-ups — lent keys', fmt(today?.lookups.lent), 'lookups-lent'],
            ['look-ups — your key', fmt(today?.lookups.own), 'lookups-own'],
            ['faction watches’ calls', fmt(today?.watch), 'watch'],
            ['alerts posted', fmt(today?.alerts), 'alerts'],
            ['DMs sent', fmt(today?.dms), 'dms'],
            ['Discord refusals', fmt(today?.refused), 'refused'],
            ['poll round — average', sec(today?.roundAvgMs), 'round-avg'],
            ['poll round — slowest', sec(today?.roundMaxMs), 'round-max'],
            ['last round', x.round ? `${sec(x.round.lastMs)} · ${x.round.factions} factions` : '—', 'round-last'],
          ]),
        ], { id: 'stats-today' }),
        box('Giveaways', [
          tiles([
            ['running', fmt(x.giveaways.running), 'gw-running'],
            ['finished', fmt(x.giveaways.finished), 'gw-finished'],
            ['cancelled', fmt(x.giveaways.cancelled), 'gw-cancelled'],
            ['entries (all time)', fmt(x.giveaways.entries), 'gw-entries'],
            ['started today', fmt(x.giveaways.startedToday), 'gw-today'],
          ]),
        ], { id: 'stats-giveaways' }),
        box(`Commands seen (last ${days.length} day${days.length === 1 ? '' : 's'}: ${fmt(x.commands.days)})`, [
          x.commands.perCommand.length
            ? withTableCsv(h('div', { class: 'table-wrap' }, h('table', { class: 'usage-table', 'data-field': 'per-command' }, h('tbody', {}, ...x.commands.perCommand.map((c) => h('tr', { 'data-command': c.name }, h('th', {}, `/${c.name}`), h('td', { class: 'num' }, fmt(c.n))))))))
            : h('p', { class: 'muted', 'data-field': 'per-command' }, 'None yet.'),
        ], { id: 'stats-commands' }),
        box('Day by day', [
          h('p', { class: 'muted small' }, 'Kept 31 days (UTC days).'),
          bars('Commands', days, (d) => d.commands, 'commands'),
          bars('Torn look-ups (all keys)', days, (d) => d.lookups.faction + d.lookups.lent + d.lookups.own, 'lookups'),
          bars('Alerts posted', days, (d) => d.alerts, 'alerts'),
          bars('Servers', days, (d) => d.servers ?? 0, 'servers'),
        ], { id: 'stats-history' }),
        h('p', { class: 'muted small' }, `As of ${new Date(x.at).toLocaleString()}. Numbers only — no server names or people.`),
      );
    })
    .catch((err: unknown) => body.replaceChildren(h('p', { class: 'notice bad' }, (err as Error).message)));
}
